import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import InnerLayout from "../../layouts/inner-layout";
import Switch from "react-switch";
import TabsComponent from "../../helper-components/tabs-component";
import AddButton, { DraftButton } from "../../buttons/button";
import successToast from "../../helper-components/success-toast";
import errorToast from "../../helper-components/error-toast";
import { moduleSettingsGetAllAction, moduleSettingsUpdateAction } from '../../../redux/actions/settings-action';
import AutoReplenishmentSettings from "./auto-replenishment-settings";

// The Auto Replenishment card lives on the billing tab (next to its Discount /
// UOM settings); if that tab isn't present it falls back to the first tab.
const AUTO_REPLENISHMENT_TAB = "Add Billing";

// Helper to render a field dynamically (no card/box, just the input row)
const renderField = (item, values, setFieldValue) => {
    switch (item.type) {
        case "toggle":
            return (
                <div key={item.label} className="flex items-center justify-between py-5 border-b border-gray-200 last:border-b-0">
                    <div className="flex flex-col">
                        <span className="font-medium text-base text-gray-900">{item.label}</span>
                        {item.description && (
                            <span className="text-xs text-gray-500 mt-1">{item.description}</span>
                        )}
                    </div>
                    <Switch
                        checked={!!values[item.label]}
                        onChange={checked => setFieldValue(item.label, checked)}
                        onColor="#3b82f6"
                        offColor="#d2d5da"
                        handleDiameter={20}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        height={24}
                        width={40}
                        className="focus:ring-2 focus:ring-primary"
                    />
                </div>
            );
        case "radio":
            return (
                <div key={item.label} className="py-3 border-b last:border-b-0">
                    <div className="font-medium text-base text-gray-900 mb-2">{item.label}</div>
                    <div className="flex flex-col gap-4">
                        {item.options.map(opt => (
                            <label
                                key={opt.value}
                                className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer border transition-all
                                    ${values[item.label] === opt.value
                                        ? 'border-primary bg-blue-50 text-primary font-semibold shadow'
                                        : 'border-gray-200 bg-white text-gray-700 hover:border-primary'}
                                `}
                            >
                                <input
                                    type="radio"
                                    name={item.label}
                                    value={opt.value}
                                    checked={values[item.label] === opt.value}
                                    onChange={() => setFieldValue(item.label, opt.value)}
                                    className="accent-primary focus:ring-2 focus:ring-primary"
                                />
                                <span className="text-sm">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            );
        default:
            return null;
    }
};

// Helper to get initial values for Formik
const getInitialValues = (settings) => {
    const values = {};
    settings.document_setting.forEach(section => {
        section.list.forEach(item => {
            values[item.label] = item.value;
        });
    });
    return values;
};

const filterValidModuleSettings = (data) => {
    // Only include settings where document_setting is an array and every section has both list and title keys
    return data.filter(item =>
        Array.isArray(item.document_setting) &&
        item.document_setting.every(
            section => section.list && section.title
        )
    );
};

const ModuleSettings = () => {
    const dispatch = useDispatch();
    const [settingsArr, setSettingsArr] = useState([]);
    const [activeTabIdx, setActiveTabIdx] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const autoReplenishmentRef = useRef(null);

    // Fetch settings on mount
    useEffect(() => {
        setLoading(true);
        dispatch(
            moduleSettingsGetAllAction(
                (res) => {
                    if (res?.data) {
                        const filtered = filterValidModuleSettings(res.data);
                        setSettingsArr(filtered);
                        setActiveTabIdx(0);
                    }
                    setLoading(false);
                },
                () => setLoading(false)
            )
        );
    }, [dispatch]);

    const reload = () => {
        setLoading(true);
        dispatch(
            moduleSettingsGetAllAction(
                (res) => {
                    if (res?.data) {
                        const filtered = filterValidModuleSettings(res.data);
                        setSettingsArr(filtered);
                        setActiveTabIdx(0);
                    }
                    setLoading(false);
                },
                () => setLoading(false)
            )
        );
    };

    if (loading) return <div className="p-6">Loading...</div>;
    if (!settingsArr.length) return <div className="p-6">No module settings found.</div>;

    const tabNames = settingsArr.map(s => s.document_name);
    const settings = settingsArr[activeTabIdx];
    const initialValues = getInitialValues(settings);
    const autoReplenishmentTab = tabNames.includes(AUTO_REPLENISHMENT_TAB)
        ? AUTO_REPLENISHMENT_TAB
        : tabNames[0];
    const showAutoReplenishment = tabNames[activeTabIdx] === autoReplenishmentTab;

    const handleSave = async (values) => {
        // Save Auto Replenishment first (it has its own API): the template
        // save below reloads the page, which remounts that card.
        if (showAutoReplenishment && autoReplenishmentRef.current) {
            if (!autoReplenishmentRef.current.validate()) return;
            setSaving(true);
            try {
                await autoReplenishmentRef.current.save();
            } catch (err) {
                setSaving(false);
                return;
            }
        }
        setSaving(true);
        // Transform values back to the API format for the current tab
        // Reconstruct the document_setting array with updated values
        const updatedDocumentSetting = settings.document_setting.map(section => ({
            ...section,
            list: section.list.map(item => ({
                ...item,
                value: values[item.label]
            }))
        }));
        const payload = {
            document_name: settings.document_name,
            document_setting: updatedDocumentSetting
        };
        dispatch(
            moduleSettingsUpdateAction(
                payload,
                (res) => {
                    setSaving(false);
                    successToast(res?.message || "Settings updated successfully");
                    reload();
                },
                (err) => {
                    setSaving(false);
                    errorToast(err?.message || "Failed to update settings");
                }
            )
        );
    };

    const handleReset = (resetForm) => {
        resetForm({ values: getInitialValues(settings) });
        autoReplenishmentRef.current?.reset();
    };

    return (
        <InnerLayout title="Settings" backBtn={true}>
            <div className=" h-full flex flex-col">
                <div className="px-6 pt-6">
                    <TabsComponent
                        itemTabs={tabNames}
                        currentTab={tabNames[activeTabIdx]}
                        handleClickfn={tabName => setActiveTabIdx(tabNames.indexOf(tabName))}
                    />
                </div>
                <Formik
                    enableReinitialize
                    initialValues={initialValues}
                    onSubmit={handleSave}
                >
                    {({ values, setFieldValue, resetForm, handleSubmit }) => (
                        <Form className="flex flex-col flex-1">
                            <div className="flex-1 px-6">
                                {settings.document_setting.map(section => (
                                    <div key={section.title} className="mb-8 bg-white border border-gray-200 rounded-xl px-7 py-6">
                                        <div className="text-2xl font-bold text-gray-900 mb-2">{section.title}</div>
                                        {section.list.map(item =>
                                            renderField(item, values, setFieldValue)
                                        )}
                                    </div>
                                ))}
                                {showAutoReplenishment && (
                                    <AutoReplenishmentSettings ref={autoReplenishmentRef} />
                                )}
                            </div>
                            <div className="flex justify-between px-6 bg-white py-4 invoice-bottom-container">
                                <DraftButton
                                    onclkFn={() => handleReset(resetForm)}
                                    text="Reset to Default"
                                    isDisabled={saving}
                                />
                                <AddButton
                                    onclkFn={handleSubmit}
                                    prefixIcon={false}
                                    text={saving ? "Saving..." : "Save Changes"}
                                    type="submit"
                                    isDisabled={saving}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </InnerLayout>
    );
};

export default ModuleSettings; 